import { Grid } from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import Actions from 'actions'
import { API } from 'aws-amplify'
import PrimaryButton from 'components/common/buttons/PrimaryButton'
import SecondaryButton from 'components/common/buttons/SecondaryButton'
import ConfirmationModal from 'components/common/ConfirmationModal'
import { Copy, TrashIcon } from 'components/common/icons'
import BaseModal from 'components/common/modals/BaseModal'
import PreviouslyUploadedStls from 'components/Patients/PatientCard/TxTrackerSetup/PreviouslyUploadedStls'
import moment from 'moment'
import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { trackEvent } from 'utils/analyticsUtils'
import { logError, logInfo } from 'utils/logUtils'

const useStyles = makeStyles({
  root: {
    height: '100%',
    width: '70vw',
    maxWidth: '100%',
    padding: 32,
    alignItems: 'space-between'
  },
  mainContent: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between'
  },
  buttonsContainer: {
    display: 'flex'
  }
})

const TxTrackerAdminModal = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const [confirmationModalType, setConfirmationModalType] = useState(null)

  const isOpen = useSelector(state => state.txTrackerReducer.txTrackerAdminModal.isOpen)
  const patient = useSelector(state => state.patientsReducer.patient)
  const grinScans = useSelector(state => state.treatmentReducer.grinScans)
  const treatment = useSelector(state => state.treatmentReducer.treatment)
  const { type: treatmentType, txTrackerStls } = treatment || {}

  const patientAppSettings = useMemo(() => JSON.parse(patient?.user?.appSettings || '{}'), [patient])
  const patientDoctorAppSettings = useMemo(() => JSON.parse(patient?.doctor?.user?.appSettings || '{}'), [patient])
  const parsedTxTrackerStls = useMemo(() => JSON.parse(txTrackerStls || '{}'), [txTrackerStls])

  const [isLoading, setIsLoading] = useState(false)
  const [isEditingFreezeVersion, setIsEditingFreezeVersion] = useState(false)
  const [isBulk, setIsBulk] = useState(false)
  const [patientIds, setPatientIds] = useState('')

  const parsedPatientIds = useMemo(
    () =>
      patientIds
        ? patientIds
            .replace(/\s/g, '')
            .replace(/\n/g, '')
            .split(',')
            .filter(p => !!p)
        : null,
    [patientIds]
  )

  const handleClose = useCallback(() => {
    dispatch(Actions.toggleTxTrackerModalOpen(false))
  }, [dispatch])

  const rerunAndDelete = useCallback(() => {
    setIsLoading(true)
    Promise.allSettled(
      (parsedPatientIds || [patient.id]).map(patientId => {
        trackEvent('Tx Tracker Admin - rerun and delete all clicked', {
          patientId: patient.id,
          partOfBulk: !!parsedPatientIds
        })
        return API.post('grinServerlessApi', '/treatment-tracker/v1/ops/rerunAndDelete', { body: { patientId } })
      })
    )
      .then(results => {
        const fulfilled = results.filter(result => result.status === 'fulfilled')
        const rejected = results.filter(result => result.status === 'rejected')

        setIsLoading(false)
        dispatch(Actions.setTreatmentTrackerModalOpen({ isOpen: false }))

        if (fulfilled.length > 0) {
          logInfo('Rerun & Delete succeeded for patients', {
            patientIds: fulfilled.map(result => result.value?.patientId),
            responses: fulfilled.map(result => result.value)
          })
        }

        if (rejected.length > 0) {
          logError('Rerun & Delete failed for some patients', {
            errors: rejected.map(result => result.reason)
          })
          console.log('rejected', JSON.stringify(rejected))
          dispatch(
            Actions.showSnackbar({
              type: 'error',
              message: 'Rerun & Delete failed for some patients. open console to view'
            })
          )
        }
      })
      .catch(ex => {
        logError('Unexpected error during rerun & delete', { error: ex })
        setIsLoading(false)
      })
  }, [patient, dispatch, parsedPatientIds])

  const initTxTracker = useCallback(() => {
    setIsLoading(true)

    Promise.allSettled(
      (parsedPatientIds || [patient.id]).map(patientId => {
        trackEvent('Tx Tracker Admin - init/rerun clicked', { patientId: patient.id, partOfBulk: !!parsedPatientIds })
        return API.post('grinServerlessApi', '/treatment-tracker/v1/ops/init', { body: { patientId } })
      })
    )
      .then(results => {
        const fulfilled = results.filter(result => result.status === 'fulfilled')
        const rejected = results.filter(result => result.status === 'rejected')

        setIsLoading(false)
        dispatch(Actions.setTreatmentTrackerModalOpen({ isOpen: false }))

        if (fulfilled.length > 0) {
          logInfo('init/rerun succeeded for patients', {
            patientIds: fulfilled.map(result => result.value?.patientId),
            responses: fulfilled.map(result => result.value)
          })
        }

        if (rejected.length > 0) {
          logError('init/rerun failed for some patients', {
            errors: rejected.map(result => result.reason)
          })
          console.log('rejected', JSON.stringify(rejected))
          dispatch(
            Actions.showSnackbar({
              type: 'error',
              message: 'init/rerun failed for some patients. open console to view'
            })
          )
        }
      })
      .catch(ex => {
        logError('Unexpected error during init/rerun', { error: ex })
        setIsLoading(false)
      })
  }, [patient, dispatch, parsedPatientIds])

  const deleteAll = useCallback(() => {
    setIsLoading(true)
    Promise.allSettled(
      (parsedPatientIds || [patient.id]).map(patientId => {
        trackEvent('Tx Tracker Admin - delete all clicked', { patientId: patient.id, partOfBulk: !!parsedPatientIds })
        return API.post('grinServerlessApi', '/treatment-tracker/v1/ops/deleteAll', { body: { patientId } })
      })
    )
      .then(results => {
        const fulfilled = results.filter(result => result.status === 'fulfilled')
        const rejected = results.filter(result => result.status === 'rejected')

        setIsLoading(false)
        dispatch(Actions.setTreatmentTrackerModalOpen({ isOpen: false }))

        if (fulfilled.length > 0) {
          logInfo('delete all succeeded for patients', {
            patientIds: fulfilled.map(result => result.value?.patientId),
            responses: fulfilled.map(result => result.value)
          })
        }

        if (rejected.length > 0) {
          logError('delete all failed for some patients', {
            errors: rejected.map(result => result.reason)
          })
          console.log('rejected', JSON.stringify(rejected))
          dispatch(
            Actions.showSnackbar({
              type: 'error',
              message: 'delete all failed for some patients. open console to view'
            })
          )
        }
      })
      .catch(ex => {
        logError('Unexpected error during delete all', { error: ex })
        setIsLoading(false)
      })
  }, [patient, dispatch, parsedPatientIds])

  const setPatientFreezeVersion = useCallback(
    version => {
      if (!version) {
        alert("You didn't provide any new freeze version")
        return
      }

      setIsLoading(true)
      Promise.allSettled(
        (parsedPatientIds || [patient.id]).map(patientId => {
          trackEvent('Tx Tracker Admin - set patient freeze version', {
            patientId: patient.id,
            partOfBulk: !!parsedPatientIds
          })
          return API.post('grinServerlessApi', '/treatment-tracker/v1/ops/setTxVersion', {
            body: { patientId, version }
          })
        })
      )
        .then(results => {
          const fulfilled = results.filter(result => result.status === 'fulfilled')
          const rejected = results.filter(result => result.status === 'rejected')

          setIsLoading(false)
          setConfirmationModalType(null)
          dispatch(Actions.setTreatmentTrackerModalOpen({ isOpen: false }))

          if (fulfilled.length > 0) {
            logInfo('set patient freeze version succeeded for patients', {
              patientIds: fulfilled.map(result => result.value?.patientId),
              responses: fulfilled.map(result => result.value)
            })
          }

          if (rejected.length > 0) {
            logError('set patient freeze version failed for some patients', {
              errors: rejected.map(result => result.reason)
            })
            console.log('rejected', JSON.stringify(rejected))
            dispatch(
              Actions.showSnackbar({
                type: 'error',
                message: 'set patient freeze version failed for some patients. open console to view'
              })
            )
          }
        })
        .catch(ex => {
          logError('Unexpected error during set patient freeze version', { error: ex })
          setIsLoading(false)
        })
    },
    [patient, dispatch, parsedPatientIds]
  )

  const deleteScanResults = useCallback(
    scanId => {
      setIsLoading(true)
      trackEvent('Tx Tracker Admin - delete scan tx results', { patientId: patient.id })

      API.post('grinServerlessApi', '/treatment-tracker/v1/ops/deleteScanResults', {
        body: {
          scanId
        }
      })
        .then(r => {
          setIsLoading(false)
          setConfirmationModalType(null)
          logInfo('Delete Scan Results succeeded', { patientId: patient.id, response: r })
        })
        .catch(ex => {
          setIsLoading(false)
          setConfirmationModalType(null)
          logError('Delete Scan Results failed', { patientId: patient.id, error: ex })
        })
    },
    [patient]
  )

  const deleteStls = useCallback(() => {
    setIsLoading(true)
    trackEvent('Tx Tracker Admin - delete treatment stls', { patientId: patient.id })

    API.post('grinServerlessApi', '/treatment-tracker/v1/ops/deleteTreatmentStls', {
      body: {
        patientId: patient.id
      }
    })
      .then(r => {
        setIsLoading(false)
        setConfirmationModalType(null)
        logInfo('Delete Treatment Stls succeeded', { patientId: patient.id, response: r })
      })
      .catch(ex => {
        setIsLoading(false)
        setConfirmationModalType(null)
        logError('Delete Treatment Stls failed', { patientId: patient.id, error: ex })
      })
  }, [patient])

  const deleteAllAlignersInfo = useCallback(() => {
    setIsLoading(true)
    Promise.allSettled(
      (parsedPatientIds || [patient.id]).map(patientId => {
        trackEvent('Tx Tracker Admin - delete all aligners info', {
          patientId: patient.id,
          partOfBulk: !!parsedPatientIds
        })
        return API.post('grinServerlessApi', '/treatment-tracker/v1/ops/deleteAlignersInfo', { body: { patientId } })
      })
    )
      .then(results => {
        const fulfilled = results.filter(result => result.status === 'fulfilled')
        const rejected = results.filter(result => result.status === 'rejected')

        setIsLoading(false)
        dispatch(Actions.setTreatmentTrackerModalOpen({ isOpen: false }))

        if (fulfilled.length > 0) {
          logInfo('delete all aligners info succeeded for patients', {
            patientIds: fulfilled.map(result => result.value?.patientId),
            responses: fulfilled.map(result => result.value)
          })
        }

        if (rejected.length > 0) {
          logError('delete all aligners info failed for some patients', {
            errors: rejected.map(result => result.reason)
          })
          console.log('rejected', JSON.stringify(rejected))
          dispatch(
            Actions.showSnackbar({
              type: 'error',
              message: 'delete all aligners info failed for some patients. open console to view'
            })
          )
        }
      })
      .catch(ex => {
        logError('Unexpected error during delete all aligners info', { error: ex })
        setIsLoading(false)
      })
  }, [patient, dispatch, parsedPatientIds])

  const handleInsertNewFreezeVersion = useCallback(e => {
    const newVersion = e.target.value
    if (!newVersion || !/V\d+\.\d+\.\d+/.test(newVersion)) {
      alert('Invalid version format. Please use the format: V2.0.1')

      return
    }
    setConfirmationModalType({ type: 'patientFreeze', version: e.target.value })
  }, [])

  return (
    <>
      <BaseModal
        open={isOpen}
        handleClose={handleClose}
        className={classes.root}
        contentClassName={classes.dialogContent}
        disableEnforceFocus
        title={t('dialogs.treatmentTracker.title')}
        titleClassName={classes.title}
      >
        <Grid className={classes.mainContent}>
          <Grid container direction="row">
            <input type="radio" name="bulk" onChange={() => setIsBulk(false)} checked={!isBulk} />
            <label>Single</label>
            <input type="radio" name="bulk" onChange={() => setIsBulk(true)} checked={isBulk} />
            <label>Bulk</label>
          </Grid>
          <br />
          {!isBulk && (
            <Grid container direction="row">
              <Grid item style={{ fontWeight: 'bold' }}>
                Patient Name:
              </Grid>
              <Grid item style={{ marginleft: 5 }}>
                {patient?.details?.name}
              </Grid>
              <Copy
                style={{ cursor: 'pointer', marginLeft: 10 }}
                onClick={() => {
                  alert('PatientId copied')
                  navigator.clipboard.writeText(patient?.id)
                }}
              />
            </Grid>
          )}
          <br />
          <Grid container direction="row">
            <Grid item style={{ fontWeight: 'bold' }}>
              Patient Freeze on version:
            </Grid>
            {isEditingFreezeVersion ? (
              <Grid item style={{ marginLeft: 5 }}>
                <input placeholder="format: V2.0.1" onBlur={handleInsertNewFreezeVersion} />
              </Grid>
            ) : (
              <Grid item style={{ marginLeft: 5 }} onClick={() => setIsEditingFreezeVersion(true)}>
                {patientAppSettings.txTrackerVersion?.modelVersionId
                  ? patientAppSettings.txTrackerVersion.modelVersionId
                  : 'None'}
              </Grid>
            )}
            {patientAppSettings.txTrackerVersion?.modelVersionId && (
              <Grid
                item
                style={{ cursor: 'pointer', marginLeft: 10 }}
                onClick={() => setConfirmationModalType({ type: 'patientFreeze' })}
              >
                <TrashIcon />
              </Grid>
            )}
          </Grid>
          <br />
          {isBulk ? (
            <>
              <Grid container direction="row">
                <textarea
                  placeholder="put patientIds. comma separated"
                  style={{ width: '50%' }}
                  onChange={e => setPatientIds(e.target.value)}
                />
              </Grid>
            </>
          ) : (
            <>
              {parsedTxTrackerStls.stls && (
                <Grid container direction="row" alignItems="center">
                  <Grid item style={{ fontWeight: 'bold' }}>
                    Uploaded STLs:
                  </Grid>
                  <Grid item style={{ marginLeft: 5 }}>
                    <PreviouslyUploadedStls
                      variant="flatten"
                      stls={parsedTxTrackerStls.stls}
                      provider={parsedTxTrackerStls.provider}
                      treatmentType={treatmentType}
                    />
                  </Grid>
                  <Grid
                    item
                    style={{ cursor: 'pointer', marginLeft: 10 }}
                    onClick={() => setConfirmationModalType({ type: 'stls' })}
                  >
                    <TrashIcon />
                  </Grid>
                </Grid>
              )}

              {patientDoctorAppSettings.txTrackerSettings?.force ||
                (patientDoctorAppSettings.txTrackerSettings?.experimental && (
                  <Grid container direction="row">
                    <Grid item style={{ fontWeight: 'bold' }}>
                      Practice Has {patientDoctorAppSettings.txTrackerSettings?.force ? 'force' : 'experimental'} on
                      version:
                    </Grid>
                    <Grid item style={{ marginLeft: 5 }}>
                      {patientDoctorAppSettings.txTrackerSettings?.force ||
                        patientDoctorAppSettings.txTrackerSettings?.experimental}
                    </Grid>
                  </Grid>
                ))}

              <br />
              {grinScans?.filter(s => !!s.treatmentTrackerStatus).length === 0 ? (
                <>No Scans With Tx Tracker Status</>
              ) : (
                grinScans
                  ?.filter(s => !!s.treatmentTrackerStatus)
                  .sort((a, b) => (a.createdAt > b.createdAt ? 1 : -1))
                  .map(s => (
                    <Grid container direction="row" key={s.createdAt} style={{ marginBottom: 5 }}>
                      <Grid item container>
                        {s.applianceIndex && (
                          <Grid item>
                            <span>Aligner: {s.applianceIndex}</span>
                          </Grid>
                        )}
                        <Grid item xs={4} style={{ paddingLeft: 10 }}>
                          <b>Scan Date:</b> ({moment(s.createdAt).format('LL')})
                        </Grid>
                        <Grid item xs={4}>
                          <b>Running date:</b>{' '}
                          {moment(JSON.parse(s.treatmentTrackerData || '{}').startedAt).format('LLL')}{' '}
                          <span
                            style={{ fontWeight: 'bold', cursor: 'pointer' }}
                            onClick={() =>
                              setConfirmationModalType({
                                type: 'patientFreeze',
                                version: JSON.parse(s.treatmentTrackerData || '{}').taskExecutionDetails?.modelVersionId
                              })
                            }
                          >
                            ({JSON.parse(s.treatmentTrackerData || '{}').taskExecutionDetails?.modelVersionId})
                          </span>
                        </Grid>
                        <Grid item xs={2}>
                          <span
                            style={{
                              fontWeight: 500,
                              color:
                                s.treatmentTrackerStatus === 'completed'
                                  ? 'green'
                                  : s.treatmentTrackerStatus === 'failed'
                                  ? 'red'
                                  : 'gray'
                            }}
                          >
                            {s.treatmentTrackerStatus}
                          </span>
                        </Grid>
                        <Grid item xs={1}>
                          <Copy
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                              alert('ScanId copied')
                              navigator.clipboard.writeText(s.id)
                            }}
                          />
                          <TrashIcon
                            style={{ cursor: 'pointer', marginLeft: 10 }}
                            onClick={() => {
                              setConfirmationModalType({ type: 'scanResults', scanId: s.id })
                            }}
                          />
                        </Grid>
                        {s.withAligner && <Grid item>' - Scan is marked With Aligner'</Grid>}
                      </Grid>
                    </Grid>
                  ))
              )}
            </>
          )}
          <br />
          <div className={classes.buttonsContainer}>
            <PrimaryButton isLoading={isLoading} label="Rerun + delete all" onClick={rerunAndDelete} />
            <SecondaryButton isLoading={isLoading} label="Init/Rerun" onClick={initTxTracker} />
            <SecondaryButton isLoading={isLoading} label="Just delete all" onClick={deleteAll} />
            <SecondaryButton isLoading={isLoading} label="Delete all aligners info" onClick={deleteAllAlignersInfo} />
          </div>
        </Grid>
      </BaseModal>
      <ConfirmationModal
        isOpen={!!confirmationModalType}
        isLoading={isLoading}
        onClose={() => {
          setConfirmationModalType(null)

          if (confirmationModalType?.type === 'patientFreeze') {
            setIsEditingFreezeVersion(false)
          }
        }}
        onConfirm={
          confirmationModalType?.type === 'patientFreeze'
            ? () => setPatientFreezeVersion(confirmationModalType?.version)
            : confirmationModalType?.type === 'scanResults'
            ? () => deleteScanResults(confirmationModalType?.scanId)
            : confirmationModalType?.type === 'stls'
            ? () => deleteStls()
            : () => {}
        }
        title={
          confirmationModalType?.type === 'patientFreeze'
            ? `Are you sure you want to set patient freeze version to: ${
                confirmationModalType?.version ? confirmationModalType?.version : 'None'
              }`
            : confirmationModalType?.type === 'scanResults'
            ? `Are you sure you want to delete the scan tx results?`
            : confirmationModalType?.type === 'stls'
            ? 'Are you sure you want to delete all of the Stls files of this patient?'
            : ''
        }
        text={''}
        additionalText={''}
        acceptButtonText={confirmationModalType?.type === 'patientFreeze' ? 'Contine' : 'Delete'}
      />
    </>
  )
}

export default TxTrackerAdminModal
